// Copyright (c) StrangeBee 2024

import { THSelectorWithFilters } from '@Molecules/THSelector/THSelector';
import React, { ReactElement } from 'react';
import { AttackPatternsOutput, TacticOutput } from 'thehive-sdk';
import { tactics } from '@Constants/procedure';
import { ColorLabel } from '@Atoms/Info/ColorLabel/ColorLabel';
import { HTMLColors } from '@Constants/colors';
import ExternalLink from '@Atoms/ExternalLink/ExternalLink';
import { THTooltip } from '@Molecules/THTooltip/THTooltip';
import { classList } from '@Helpers/className';
import { ICONS } from '@Constants/iconsApp';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import { useLocalMessage } from '@Helpers/locale';
import { THInput } from '@Molecules/Inputs/THInput/THInput';

import './TTPSelector.scss';

interface TTPSelectorProps {
    attackPatterns: AttackPatternCustom[];
    tactics: TacticOutput[];
    isLoading?: boolean;
    disabled?: boolean;
    single?: boolean;
    onChange?: (value: AttackPatternCustom[], itemAdded?: AttackPatternCustom) => void;
    value?: AttackPatternCustom[];
    defaultTacticFilter?: string;
    defaultTechniqueFilter?: string;
}
type AttackPatternCustom = AttackPatternsOutput & {
    tacticParent: string;
    isTactic?: boolean;
    refKey?: string;
};

export const TTPSelector = ({
    attackPatterns,
    tactics,
    isLoading = false,
    disabled,
    single,
    onChange,
    value,
    defaultTacticFilter,
    defaultTechniqueFilter,
}: TTPSelectorProps): ReactElement => {
    const i = useLocalMessage();

    const tacticFilter = {
        input: (
            <THSelect
                allowClear
                placeholder={i('procedure.create.tactic.placeholder')}
                errorless
                defaultValue={defaultTacticFilter}
            >
                {tactics
                    ?.sort((a, b) => (a.order || 0) - (b.order || 0))
                    .map(({ _id, shortName, name }) => (
                        <THSelect.Option key={_id} value={shortName}>
                            {name}
                        </THSelect.Option>
                    ))}
            </THSelect>
        ),
        matchValue: (input: string, item: AttackPatternCustom): boolean => {
            return item.tacticParent == input;
        },
    };
    const techniqueFilter = {
        input: (
            <THInput
                placeholder={i('procedure.create.technique.filter-placeholder')}
                errorless
                defaultValue={defaultTechniqueFilter}
            />
        ),
        matchValue: (input: string, item: AttackPatternCustom): boolean => {
            const str = `${item.patternId} - ${item.name} - ${item.tacticParent}`.toLowerCase();
            return str.includes(input.toLowerCase());
        },
    };

    return (
        <div className={classList('ttp-selector', { disabled })}>
            <THSelectorWithFilters<AttackPatternCustom>
                dataSource={attackPatterns}
                refKey="refKey"
                className="ttp-selector__list"
                showSelected={true}
                loading={isLoading}
                filters={[tacticFilter, techniqueFilter]}
                onChange={onChange}
                // get the same reference in THSelector to select default values
                value={attackPatterns.filter((pattern) =>
                    value?.some((item) => `${item._id}-${item.tacticParent}` === pattern.refKey),
                )}
                disabled={disabled}
                single={single}
                watchValue
            >
                {(props, selected): ReactElement => {
                    return <TTPListItem {...props} selected={selected} />;
                }}
            </THSelectorWithFilters>
        </div>
    );
};

interface TTPListItemProps {
    name: string;
    tacticParent: string;
    patternId: string;
    description?: string;
    url: string;
    selected?: boolean;
}

const TTPListItem = React.memo(
    ({ name, tacticParent, patternId, description, url, selected = false }: TTPListItemProps): ReactElement => {
        const style = tacticParent in tactics ? { color: tactics[tacticParent].color } : {};

        return (
            <div
                className={classList('ttp-selector__item', {
                    'item-indent': !!~patternId.indexOf('.'),
                })}
            >
                <span className="item-select">
                    {selected ? <ICONS.CheckCircleTwoTone /> : <ICONS.MinusCircleOutlined />}
                    <span className="item-select__name">{`${patternId} - ${name}`}</span>
                </span>
                <span className="item-description">
                    <ColorLabel
                        className="item-select__tactic"
                        label={tacticParent}
                        color={style.color || HTMLColors.DEFAULT}
                    />
                    <THTooltip title={description}>
                        <ICONS.QuestionCircleFilled />
                    </THTooltip>

                    <ExternalLink url={url}>
                        <ICONS.ExternalLinkIcon />
                    </ExternalLink>
                </span>
            </div>
        );
    },
);
TTPListItem.displayName = 'TTPListItem';
